import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { EnfafTextareaDarkComponent } from '../../../../components/enfaf-textarea-dark/enfaf-textarea-dark.component';
import { EnfafDatetimePickerDarkComponent } from '../../../../components/enfaf-datetime-picker-dark/enfaf-datetime-picker-dark.component';
import { EnfafInputFileDraggerDarkComponent } from '../../../../components/enfaf-input-file-dragger-dark/enfaf-input-file-dragger-dark.component';
import { CourseTfmConfig, TfmSupportFile } from '../../../../models/interfaces/tfm.interface';
import { CustomFile, CustomUploadedFile } from '../../../../models/interfaces/custom_file.interface';

@Component({
  selector: 'app-admin-tfm-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatExpansionModule,
    EnfafButtonComponent,
    EnfafSwitchDarkComponent,
    EnfafTextareaDarkComponent,
    EnfafDatetimePickerDarkComponent,
    EnfafInputFileDraggerDarkComponent
  ],
  templateUrl: './admin-tfm-config.component.html',
  styleUrl: './admin-tfm-config.component.scss'
})
export class AdminTfmConfigComponent implements OnInit, OnChanges {
  @Input() courseId!: string;
  @Output() configSaved = new EventEmitter<CourseTfmConfig>();
  /** Emits true when an existing config is loaded, false when none exists yet. */
  @Output() configExists = new EventEmitter<boolean>();

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private toastService = inject(ToastService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);

  form!: FormGroup;
  config: CourseTfmConfig | null = null;
  isLoading = false;
  isSaving = false;

  isSavingEnabled = false;
  isUploadingFile = false;
  sectionExpanded = true;
  draftExpanded = true;
  finalExpanded = true;

  draftSupportFiles: TfmSupportFile[] = [];
  finalSupportFiles: TfmSupportFile[] = [];

  cleanDraftFiles = new Subject<void>();
  cleanFinalFiles = new Subject<void>();

  get draftCurrentFiles(): CustomUploadedFile[] {
    return this.draftSupportFiles.map((f) => ({ filename: f.name, path: f.url, presignedUrl: f.url }));
  }

  get finalCurrentFiles(): CustomUploadedFile[] {
    return this.finalSupportFiles.map((f) => ({ filename: f.name, path: f.url, presignedUrl: f.url }));
  }

  ngOnInit(): void {
    this.initForm();
    this.form
      .get('enabled')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val: boolean) => {
        if (this.isLoading) return;
        this.saveEnabled(val);
      });
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseId'] && !changes['courseId'].firstChange) {
      this.loadConfig();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      enabled: [false],
      draftDescription: [''],
      draftOpenDate: [null],
      draftDueDate: [null],
      finalDescription: [''],
      finalOpenDate: [null],
      finalDueDate: [null]
    });
  }

  private async loadConfig(): Promise<void> {
    if (!this.courseId) return;
    try {
      this.isLoading = true;
      const response = await firstValueFrom(this.apiService.getTfmConfig(this.courseId));
      if (response?.data) {
        this.config = response.data;
        this.draftSupportFiles = response.data.draftSupportFiles ?? [];
        this.finalSupportFiles = response.data.finalSupportFiles ?? [];
        this.form.patchValue({
          enabled: response.data.enabled ?? false,
          draftDescription: response.data.draftDescription ?? '',
          draftOpenDate: response.data.draftOpenDate ?? null,
          draftDueDate: response.data.draftDueDate ?? null,
          finalDescription: response.data.finalDescription ?? '',
          finalOpenDate: response.data.finalOpenDate ?? null,
          finalDueDate: response.data.finalDueDate ?? null
        });
      }
    } catch {
      // Config may not exist yet — that is fine
    } finally {
      this.isLoading = false;
      this.configExists.emit(this.config !== null);
    }
  }

  private async saveEnabled(enabled: boolean): Promise<void> {
    if (this.isSavingEnabled || !this.courseId) return;
    try {
      this.isSavingEnabled = true;
      const response = await firstValueFrom(this.apiService.toggleTfmEnabled(this.courseId, enabled));
      if (response?.success) {
        this.config = response.data;
        const message = enabled ? 'TFM activado correctamente.' : 'TFM desactivado correctamente.';
        this.toastService.presentSuccessToast(message);
        this.configSaved.emit(this.config!);
      }
    } catch (err) {
      console.error('[AdminTfmConfig] saveEnabled error:', err);
      this.toastService.presentErrorToast('Error al guardar la configuración TFM.');
      this.form.get('enabled')!.setValue(!enabled, { emitEvent: false });
    } finally {
      this.isSavingEnabled = false;
    }
  }

  async saveConfig(): Promise<void> {
    if (this.isSaving) return;
    const payload = this.form.value;

    if (payload.draftOpenDate && payload.draftDueDate) {
      if (new Date(payload.draftDueDate) <= new Date(payload.draftOpenDate)) {
        this.toastService.presentErrorToast('La fecha de cierre debe ser posterior a la de apertura.');
        return;
      }
    }

    if (payload.draftOpenDate && payload.draftDueDate && payload.finalOpenDate && payload.finalDueDate) {
      const draftOpen = new Date(payload.draftOpenDate);
      const draftClose = new Date(payload.draftDueDate);
      const finalOpen = new Date(payload.finalOpenDate);
      const finalClose = new Date(payload.finalDueDate);
      // Two ranges [A,B] and [C,D] overlap when A < D && B > C
      if (draftOpen < finalClose && draftClose > finalOpen) {
        this.toastService.presentErrorToast('Las fechas del borrador no pueden solaparse con las del TFM Final.');
        return;
      }
    }

    try {
      this.isSaving = true;
      const response = await firstValueFrom(this.apiService.upsertTfmConfig(this.courseId, payload));
      if (response?.success) {
        this.config = response.data;
        this.toastService.presentSuccessToast('Configuración TFM guardada.');
        this.configSaved.emit(this.config!);
      }
    } catch {
      this.toastService.presentErrorToast('Error guardando configuración TFM.');
    } finally {
      this.isSaving = false;
    }
  }

  async onSupportFilesDropped(files: CustomFile[], type: 'draft' | 'final'): Promise<void> {
    if (!files.length) return;
    try {
      this.isUploadingFile = true;
      for (const customFile of files) {
        if (!customFile.file) continue;
        const formData = new FormData();
        formData.append('file', customFile.file);
        formData.append('type', type);
        const response = await firstValueFrom(this.apiService.uploadTfmSupportFile(this.courseId, formData));
        if (response?.success) {
          if (type === 'draft') this.draftSupportFiles = [...this.draftSupportFiles, response.data];
          else this.finalSupportFiles = [...this.finalSupportFiles, response.data];
        }
      }
      if (type === 'draft') this.cleanDraftFiles.next();
      else this.cleanFinalFiles.next();
    } catch {
      this.toastService.presentErrorToast('Error subiendo archivo de apoyo.');
    } finally {
      this.isUploadingFile = false;
    }
  }

  async onSupportFileDeleted(uploadedFile: CustomUploadedFile, type: 'draft' | 'final'): Promise<void> {
    const files = type === 'draft' ? this.draftSupportFiles : this.finalSupportFiles;
    const tfmFile = files.find((f) => f.url === uploadedFile.path);
    if (!tfmFile) return;
    await this.deleteSupportFile(tfmFile, type);
  }

  async deleteSupportFile(file: TfmSupportFile, type: 'draft' | 'final'): Promise<void> {
    const confirmed = await this.alertService.presentConfirmRemoveActionAlert({
      title: 'Eliminar archivo',
      message: `¿Seguro que quieres eliminar "${file.name}"?`,
      cancelText: 'Cancelar',
      confirmText: 'Eliminar'
    });
    if (!confirmed) return;
    try {
      await firstValueFrom(this.apiService.deleteTfmSupportFile(this.courseId, file.key));
      if (type === 'draft') this.draftSupportFiles = this.draftSupportFiles.filter((f) => f.key !== file.key);
      else this.finalSupportFiles = this.finalSupportFiles.filter((f) => f.key !== file.key);
      this.toastService.presentSuccessToast('Archivo eliminado.');
    } catch {
      this.toastService.presentErrorToast('Error eliminando archivo.');
    }
  }

  toggleDraft(): void {
    this.draftExpanded = !this.draftExpanded;
  }

  toggleFinal(): void {
    this.finalExpanded = !this.finalExpanded;
  }

  goToDrafts(): void {
    void this.router.navigate(['/god/tfm/courses', this.courseId, 'drafts']);
  }

  goToFinals(): void {
    void this.router.navigate(['/god/tfm/courses', this.courseId, 'finals']);
  }

  goToAudit(): void {
    void this.router.navigate(['/god/tfm/courses', this.courseId, 'audit']);
  }
}
